import { useEffect, useState } from 'react'
import { sdk } from '@farcaster/miniapp-sdk'
import { supabase } from './lib/supabase'
import { BASE_APP_CLIENT_FID, RECEIVER, UNIT_USDC } from './lib/config'
import { useMiniKit } from '@coinbase/onchainkit/minikit'
import DeliveryFlow from './components/DeliveryFlow'
// import { recordPurchase } from './lib/record'
import { addMiniAppOnFirstInteraction } from './addMiniAppOnFirstInteraction'
import { countPinsToParksByFid } from './lib/pins'

import './App.css'

// Declarações de tipo para propriedades customizadas do window
declare global {
  interface Window {
    __HOST__?: string;
    openPurchaseModal?: () => void;
  }
}

// Hook para detectar Base App usando clientFid oficial
function useIsBaseApp() {
  const { context } = useMiniKit();
  // Detecção oficial do Base App via clientFid
  const isBaseApp = context?.client?.clientFid === BASE_APP_CLIENT_FID;
  return isBaseApp;
}

// Hook correto - chama ready() com disableNativeGestures
function useMiniAppReady() {
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const isInMiniApp = await (sdk.isInMiniApp as (ms?: number) => Promise<boolean>)(3000);
        const embedded = window.parent !== window || !!(window as any).ReactNativeWebView;
        if (isInMiniApp || embedded) {
          await sdk.actions.ready({ disableNativeGestures: true }); 
          if (mounted) console.log('🔄 Farcaster SDK ready com disableNativeGestures');
        }
      } catch (error) {
        if (mounted) console.log('⚠️ Farcaster SDK ready error:', error);
      } 
    })();
    return () => { mounted = false };
  }, []);
}

export default function App() {
  const [selectedCurrency, setSelectedCurrency] = useState<'ETH' | 'USDC'>('ETH')
  const [AddressInputComponent, setAddressInputComponent] = useState<any>(null)

  // SEM DETECÇÃO DE MOBILE/DESKTOP - TUDO IGUAL PARA TODOS
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [purchaseData, setPurchaseData] = useState<any>(null)
  
  // Estado para pop-up de sucesso
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)

  // Estado para pop-up da star (pins info)
  const [showPinsPopup, setShowPinsPopup] = useState(false)

  // Estado para carregamento dinâmico do BaseFlow
  const [BaseFlow, setBaseFlow] = useState<null | React.ComponentType<any>>(null);

  // Detectar Base App usando clientFid oficial
  const isBaseApp = useIsBaseApp();

  // Para Base App: usa MiniKit (client-agnostic)
  // Para Farcaster: usa sdk.context (fallback se necessário)
  const { context: minikitContext, isFrameReady, setFrameReady } = useMiniKit();
  const baseAppUser = minikitContext?.user;

  // Chamar setFrameReady() sempre para popular o contexto
  // (necessário para detectar isBaseApp e obter user)
  useEffect(() => {
    if (!isFrameReady && setFrameReady) {
      setFrameReady();
    }
  }, [isFrameReady, setFrameReady]);

  // Estados para UserDisplay e Onboarding na homepage
  const [pinsCount, setPinsCount] = useState<number | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Registrar usuário no Supabase (Base App ou Farcaster via MiniKit)
  useEffect(() => {
    const registerUser = async () => {
      if (baseAppUser?.fid) {
        try {
          const userData = {
            fid: baseAppUser.fid,
            username: baseAppUser.username || null,
            added: false,
            last_client: isBaseApp ? 'base_app' : 'farcaster'
          };
          
          await supabase
            .from('users')
            .upsert(userData, { onConflict: 'fid' })

        } catch (e) {
          // Silencioso
        }
      }
    };
    registerUser();
  }, [isBaseApp, baseAppUser]);

  // Carregar AddressInput sempre (sem diferenciação)
  useEffect(() => {
    console.log('🔍 Carregando AddressInput para todos os devices...');
    
    if (!AddressInputComponent) {
      import('./components/AddressInput').then((module) => {
        setAddressInputComponent(() => module.default);
        console.log('✅ AddressInput carregado para todos os devices');
      }).catch((error) => {
        console.error('❌ Erro ao carregar AddressInput:', error);
      });
    }
  }, [AddressInputComponent]);

  // Carregar BaseFlow dinamicamente se for Base App
  useEffect(() => {
    if (isBaseApp) {
      import('./components/DeliveryFlow.base').then(m => {
        setBaseFlow(() => m.default);
      }).catch(console.error);
    }
  }, [isBaseApp]);

  // Usar o hook para garantir que o Mini App está sempre ready
  useMiniAppReady();



  // Inicialização do Farcaster SDK (separada do ready())
  useEffect(() => {
    const initFarcaster = async () => {
      try {
        const context = await sdk.context
        
        if (context?.features?.cameraAndMicrophoneAccess) {
          console.log('📱 Device permite câmera/mic');
        }
        
        if (context?.user?.fid) {
          await registerUser(context.user, 'farcaster')
        }
        
        sdk.on('miniappAdded' as any, async () => {
          try {
            if (!context?.user?.fid) return;
            const { error } = await supabase
              .from('users')
              .update({ added: true })
              .eq('fid', String(context.user.fid))
            
            if (error) throw error;
          } catch (e) {
            // Silencioso
          }
        })
        
        // Mini App sempre usa carteira nativa
        console.log('🚀 Mini App Farcaster detectado - usando carteira nativa');
        
      } catch (error) {
        console.error('❌ Erro ao inicializar Farcaster:', error)
      }
    }

    initFarcaster()
  }, [])

  // Add Mini App na primeira interação (funciona em desktop e mobile)
  useEffect(() => {
    // Add Mini App na primeira interação (funciona em desktop e mobile)
    const detach = addMiniAppOnFirstInteraction({ timeoutMs: 5000 })
    return () => { detach() }
  }, [])

  // Listener para evento de compra bem-sucedida: grava no Supabase e abre popup
  useEffect(() => {
    const onPurchaseSuccess = async (e: Event) => {
      const d = (e as CustomEvent).detail || {}
      const f = (window as any).__PENDING_FORM__ || {};
      (window as any).__PENDING_FORM__ = null;

      try {
        const mode = f.mode ?? d.mode ?? 'parks'
        const qty = Number(f.qty ?? d.qty ?? 1)
        const source = d.source ?? f.source ?? (isBaseApp ? 'base' : 'farcaster')
        const paidViaBasePay = source === 'base'
        const currency = paidViaBasePay ? 'USDC' : (f.currency ?? d.currency ?? 'USDC')
        const addressFromEvent = d.address
        const addressFromForm = (mode === 'delivery') ? {
            name: f.name,
            email: f.email,
            address_line1: f.address_line1,
            address_line2: f.address_line2,
            city: f.city,
            region: f.region,
            postal_code: f.postal_code,
            country: f.country,
            place_id: f.place_id,
            lat: f.lat,
            lng: f.lng
          } : undefined

        const payload = {
          source,
          chain: 'base',
          wantsReceive: mode === 'delivery',
          quantity: qty,
          currency,
          tx: d.hash,
          mode,
          context: { host: source === 'base' ? 'baseapp' : 'farcaster' },
          address: addressFromEvent ?? addressFromForm
        };

        const { recordPurchase } = await import('./lib/record')
        await recordPurchase(payload)
        setShowSuccessPopup(true)
      } catch (err) {
        console.error('Supabase write failed:', err)
        setShowSuccessPopup(true)
      }
    }

    window.addEventListener('purchase:success', onPurchaseSuccess as any)
    return () => window.removeEventListener('purchase:success', onPurchaseSuccess as any)
  }, [])

  // Listener para evento purchase:request - inicia o pagamento
  useEffect(() => {
    const handlePurchaseRequest = async (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      const { currency, qty, mode } = detail
      
      if (!currency || !qty) {
        console.error('❌ purchase:request sem currency ou qty:', detail)
        return
      }

      try {
        console.log('🚀 purchase:request recebido:', { currency, qty, mode })
        
        // Importar Base Pay
        const { pay } = await import('@base-org/account')
        
        const to = RECEIVER
        const testnet = false
        // Base Pay settles in USDC. ETH CTA is the same $15 unit price.
        const totalAmountUsdc = (UNIT_USDC * qty).toFixed(2)
        
        console.log('💰 Iniciando pagamento:', { currency, qty, totalAmountUsdc })
        
        // Configurar payerInfo baseado no mode
        const payerInfo: any = {
          requests: [
            { type: 'email' },
            { type: 'phoneNumber', optional: true }
          ]
        }
        
        // Se for delivery, pedir endereço físico
        if (mode === 'delivery') {
          payerInfo.requests.push({ type: 'physicalAddress' })
        }
        
        // Iniciar pagamento Base Pay
        const payment = await pay({
          amount: totalAmountUsdc, // Base Pay sempre usa USDC
          to,
          testnet,
          payerInfo
        })
        
        console.log('✅ Pagamento iniciado:', payment.id)
        
        // Verificar status do pagamento
        const { getPaymentStatus } = await import('@base-org/account')
        let status
        do {
          await new Promise(r => setTimeout(r, 1500))
          status = await getPaymentStatus({ id: payment.id, testnet })
          console.log('📊 Status do pagamento:', status.status)
        } while (status.status === 'pending')
        
        if (status.status === 'completed') {
          console.log('✅ Pagamento completado!')
          
          // Disparar evento de sucesso com hash da transação
          const form = (window as any).__PENDING_FORM__ || {}
          window.dispatchEvent(new CustomEvent('purchase:success', {
            detail: {
              hash: payment.id,
              source: 'base',
              currency: 'USDC',
              qty: form.qty || qty,
              mode: form.mode || mode
            }
          }))
        } else {
          console.error('❌ Pagamento falhou com status:', status.status)
          alert(`Pagamento falhou: ${status.status}`)
        }
      } catch (error) {
        console.error('❌ Erro ao processar purchase:request:', error)
        alert(`Erro no pagamento: ${error instanceof Error ? error.message : String(error)}`)
      }
    }

    window.addEventListener('purchase:request', handlePurchaseRequest as any)
    return () => window.removeEventListener('purchase:request', handlePurchaseRequest as any)
  }, [])

  // Registrar usuário no Supabase
  const registerUser = async (user: any, client: 'farcaster' | 'base_app' = 'farcaster') => {
    try {
      const userData = {
        fid: String(user.fid),
        username: user.username || null,
        // display_name: user.displayName || null, // COLUNA NÃO EXISTE NA TABELA
        // pfp_url: user.pfp?.url || null, // COLUNA NÃO EXISTE NA TABELA
        added: false,
        last_client: client,
        created_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('users')
        .upsert(userData, { onConflict: 'fid' })

      if (error) throw error;
      
    } catch (e) {
      // Silencioso
    }
  }


  // Abrir modal de compra
  const openPurchaseModal = () => {
    // Preservar posição atual da página
    const currentScrollY = window.scrollY;
    
    setShowPurchaseModal(true);
    
    // Restaurar posição após o modal abrir
    // Usar requestAnimationFrame para garantir que o DOM foi atualizado
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  // Fechar modal de compra
  const closePurchaseModal = () => {
    setShowPurchaseModal(false);
    // Não resetar dados para manter posição da página
  };

  // Função para fechar todos os pop-ups em cascata
  const closeAllPopups = () => {
    setShowSuccessPopup(false);
    setShowPurchaseModal(false);
    // Resetar dados de compra se necessário
    setPurchaseData(null);
    console.log('🔄 Fechando todos os pop-ups em cascata');
  };



  const handleDeliveryChoice = (wantsDelivery: boolean) => {
    if (wantsDelivery) {
      // Mostrar formulário de endereço
      setPurchaseData((prev: any) => ({
        ...prev,
        showAddressForm: true
      }));
    } else {
      // Não quer delivery, ir direto para pagamento
      setPurchaseData((prev: any) => ({
        ...prev,
        showAddressForm: true
      }));
    }
  };

  // Expor função globalmente para o HTML
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).openPurchaseModal = openPurchaseModal
    }
  }, [])

  // Simplificado: sempre assume Mini App para evitar travamento

  // Chamar setFrameReady() para Base App
  useEffect(() => {
    if (isBaseApp && !isFrameReady) {
      setFrameReady();
    }
  }, [isBaseApp, isFrameReady, setFrameReady]);

  // Detectar preferência de tema do sistema
  useEffect(() => {
    const checkTheme = () => {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(prefersDark);
    };
    
    checkTheme();
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkTheme);
    
    return () => mediaQuery.removeEventListener('change', checkTheme);
  }, []);

  // Mostrar onboarding sempre que abrir o app (apenas Base App)
  // TEMPORARIAMENTE DESABILITADO
  // useEffect(() => {
  //   if (isBaseApp) {
  //     setShowOnboarding(true);
  //   }
  // }, [isBaseApp]);

  // Carregar contagem de pins para Base App
  useEffect(() => {
    if (isBaseApp && baseAppUser?.fid) {
      countPinsToParksByFid(baseAppUser.fid)
        .then(count => {
          const finalCount = typeof count === 'number' ? count : 0;
          setPinsCount(finalCount);
        })
        .catch(() => {
          setPinsCount(0);
        });
    } else {
      setPinsCount(0);
    }
  }, [isBaseApp, baseAppUser?.fid]);

  return (
    <div className="App">
      <style>
        {`
          @keyframes rotateRight {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          /* Garantir que App e elementos React tenham background preto */
          .App {
            pointer-events: none;
            background: transparent !important;
          }
          .App > * {
            pointer-events: auto;
          }
          
          body,
          html {
            background: #000000 !important;
          }
          #root,
          #react-root {
            background: transparent !important;
            pointer-events: none;
          }
          
          /* Garantir que #app esteja visível (HTML estático) */
          #app {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: relative;
            z-index: 1;
          }
          
          /* React root para modais e popups */
          #react-root {
            position: relative;
            z-index: 1000;
            pointer-events: none;
          }
          
          #react-root > * {
            pointer-events: auto;
          }
        `}
      </style>

      {/* User Display no topo da homepage - apenas no Base App e se tiver dados do usuário */}
      {isBaseApp && baseAppUser && (baseAppUser.displayName || baseAppUser.username) && (
        <>
          <div style={{
            position: 'fixed',
            top: '12px',
            left: '12px',
            right: '12px',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {baseAppUser.pfpUrl && (
              <img 
                src={baseAppUser.pfpUrl} 
                alt="Avatar" 
                style={{ 
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }} 
              />
            )}
              <span style={{
                color: '#ffffff',
                fontFamily: 'Chalkboard, sans-serif',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                {baseAppUser.displayName || baseAppUser.username}
              </span>
            </div>
            
            {/* Star com número de pins */}
            <div 
              onClick={() => setShowPinsPopup(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                width: '40px',
                height: '40px'
              }}
            >
              <img 
                src="/imgs/star.svg" 
                alt="Pins" 
                style={{ 
                  width: '40px',
                  height: '40px',
                  filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
                  animation: 'rotateRight 20s linear infinite',
                  position: 'absolute'
                }} 
              />
              <span style={{
                color: '#ffffff',
                fontFamily: 'Arial Black, sans-serif',
                fontSize: '14px',
                fontWeight: 'bold',
                position: 'absolute',
                zIndex: 1
              }}>
                {pinsCount !== null ? pinsCount : '...'}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Pop-up da star (pins info) - apenas Base App */}
      {isBaseApp && showPinsPopup && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#000000',
            border: '2px solid #f9d70b',
            borderRadius: '0px',
            padding: '50px 30px 30px 30px',
            maxWidth: '400px',
            width: '100%',
            color: '#ffffff',
            fontFamily: 'Coinbase Mono Light, monospace',
            textAlign: 'center',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowPinsPopup(false)}
              style={{
                position: 'absolute',
                top: '3px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#ffffff',
                cursor: 'pointer',
                padding: '8px',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
            
            <div style={{
              fontSize: '18px',
              lineHeight: '1.6',
              marginBottom: '30px',
              color: '#ffffff'
            }}>
              {pinsCount !== null && pinsCount > 0 ? (
                <>You already sent {pinsCount} {pinsCount === 1 ? 'pin' : 'pins'} to the parks!</>
              ) : (
                <>You have not sent any pins to the parks yet.</>
              )}
            </div>

            <button
              onClick={() => {
                setShowPinsPopup(false);
                openPurchaseModal();
              }}
              style={{
                background: '#f9d70b',
                border: 'none',
                color: '#000000',
                padding: '17px 24px 12px 24px',
                fontSize: '32px',
                fontFamily: 'Chalkboard, sans-serif',
                fontWeight: 'bold',
                cursor: 'pointer',
                borderRadius: '0px',
                textTransform: 'uppercase',
                minWidth: '120px',
                minHeight: '44px',
                width: '100%'
              }}
            >
              {pinsCount !== null && pinsCount > 0 ? 'SEND MORE!' : 'SEND NOW'}
            </button>
          </div>
        </div>
      )}

      {/* Onboarding popup - apenas Base App */}
      {isBaseApp && showOnboarding && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: isDarkMode ? '#000000' : '#ffffff',
            border: '2px solid #f9d70b',
            borderRadius: '0px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%',
            color: isDarkMode ? '#ffffff' : '#000000',
            fontFamily: 'Coinbase Mono Light, monospace',
            textAlign: 'center',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                setShowOnboarding(false);
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: isDarkMode ? '#ffffff' : '#000000',
                cursor: 'pointer',
                padding: '8px',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
            
            <h2 style={{
              fontFamily: 'Chalkboard, sans-serif',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#f9d70b',
              marginBottom: '20px',
              textTransform: 'uppercase'
            }}>
              Welcome to the Mission!
            </h2>

            <div style={{
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '30px',
              textAlign: 'left'
            }}>
              <p style={{ marginBottom: '15px' }}>
                <strong style={{ color: '#f9d70b' }}>The Plan:</strong> We made limited edition pins. Steamboat Willie with Nouns glasses.
              </p>
              <p style={{ marginBottom: '15px' }}>
                <strong style={{ color: '#f9d70b' }}>How it works:</strong>
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
                <li style={{ marginBottom: '10px' }}>Choose to receive your pin at home (YES) or send it directly to the parks (NO)</li>
                <li style={{ marginBottom: '10px' }}>Select quantity and pay with ETH or USDC</li>
                <li style={{ marginBottom: '10px' }}>We'll quietly sneak pins into Disney parks</li>
              </ul>
              <p style={{ fontSize: '12px', color: isDarkMode ? '#cccccc' : '#666666', fontStyle: 'italic' }}>
                Steamboat Willie entered the public domain in 2024. Nouns has always been cc0. Join the mission!
              </p>
            </div>

            <button
              onClick={() => {
                setShowOnboarding(false);
              }}
              style={{
                background: '#f9d70b',
                border: 'none',
                color: '#000000',
                padding: '12px 24px',
                fontSize: '18px',
                fontFamily: 'Chalkboard, sans-serif',
                fontWeight: 'bold',
                cursor: 'pointer',
                borderRadius: '0px',
                textTransform: 'uppercase',
                minWidth: '120px',
                minHeight: '44px',
                width: '100%'
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
      
      {/* Modal de Compra */}
      {showPurchaseModal && (
        <div className="purchase-modal-overlay">
          <div>
                        {/* FLUXO DE DELIVERY - YES/NO + FORMS */}
                        {isBaseApp && BaseFlow
                          ? <BaseFlow onClose={closePurchaseModal} />
                          : <DeliveryFlow onClose={closePurchaseModal} />
                        }
                 
                 {/* FLUXO DE ENTREGA - REMOVIDO PARA EVITAR TRAVAMENTO */}
            {false && (
              <>
                {!purchaseData.showAddressForm ? (
                  // Primeira tela: Escolha de entrega
                  <div className="purchase-choice">
                    <h3>VOCÊ QUER RECEBER SEUS PINS EM CASA?</h3>
                    
                    {/* Seletor de Moeda */}
                    <div className="currency-selector">
                      <label>Escolha a moeda:</label>
                      <select 
                        value={selectedCurrency} 
                        onChange={(e) => setSelectedCurrency(e.target.value as 'ETH' | 'USDC')}
                      >
                        <option value="ETH">0.005 ETH</option>
                        <option value="USDC">15 USDC</option>
                      </select>
                    </div>
                    
                    {/* Botões de compra */}
                    <div className="purchase-buttons">
                      <button 
                        className="btn-yes"
                        onClick={() => handleDeliveryChoice(true)}
                      >
                        YES
                      </button>
                      <button 
                        className="btn-no"
                        onClick={() => handleDeliveryChoice(false)}
                      >
                        NO, SEND THEM TO THE PARKS
                      </button>
                    </div>
                    <button className="btn-close" onClick={closePurchaseModal}>
                      ✕
                    </button>
                  </div>
                ) : (
                  // Segunda tela: Pagamento
                  <DeliveryFlow 
                  onClose={closePurchaseModal}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      {/* 🧪 BASE PAY TEST - SEÇÃO DE TESTE (OCULTA) */}
      {/* 
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.9)',
        border: '2px solid #0066ff',
        borderRadius: '10px',
        padding: '15px',
        maxWidth: '400px',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <h3 style={{
          color: '#0066ff',
          margin: '0 0 15px 0',
          fontSize: '16px',
          textAlign: 'center',
          borderBottom: '1px solid #0066ff',
          paddingBottom: '10px'
        }}>
          🧪 Base Pay Test
        </h3>
        <BasePayTest />
      </div>
      */}
      
      {/* Modal para adicionar Mini App */}
      {/* REMOVIDO */}
      
      {/* Botão fallback para add Mini App - REMOVIDO */}
      
      {/* Pop-up de Sucesso */}
      {showSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          <div style={{
            background: 'transparent',
            border: 'none',
            borderRadius: '0px',
            padding: '40px',
            maxWidth: '365px !important',
            minWidth: '365px',
            width: '365px',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Botão X para fechar */}
            <button
              onClick={closeAllPopups}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#f9d70b',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
            >
              ×
            </button>

            {/* Título THANK YOU, PAL! */}
            <h1 style={{
              fontFamily: 'Chalkboard, sans-serif',
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#ffffff',
              margin: '0 0 30px 0',
              transform: 'rotate(2deg)',
              lineHeight: '1.2',
              whiteSpace: 'nowrap',
              textAlign: 'center'
            }}>
              THANK YOU, PAL!
            </h1>

            {/* Texto de agradecimento */}
            <div style={{
              fontFamily: 'Coinbase Mono Light, monospace',
              fontSize: '16px',
              lineHeight: '1.6',
              color: '#ffffff',
              transform: 'rotate(-1deg)',
              textAlign: 'center',
              marginTop: '-5px'
            }}>
              Getting it at home? Watch for a confirmation email soon. Follow{' '}
              <a 
                href="https://x.com/jp_costa" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  fontFamily: 'Arial Black, sans-serif',
                  color: '#f9d70b',
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}
              >
                @jp_costa
              </a>
              {' '}to stay updated and share to bring your friends on the mission!
            </div>

            {/* Star SVG abaixo do texto */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
              marginTop: '20px',
              marginLeft: '1px'
            }}>
              <img 
                src="/imgs/star.svg" 
                alt="Star" 
                style={{
                  width: '150px',
                  height: '150px',
                  filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
                  animation: 'rotateRight 40s linear infinite'
                }}
              />
            </div>

            {/* Botão SHARE NOW */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px'
            }}>
              <button 
                onClick={async () => {
                  try {
                    console.log('🚀 Iniciando composeCast...');
                    
                    // Textos diferentes para cada plataforma
                    const castText = isBaseApp
                      ? "I'm joining the mission to sneak Nouns into the happiest place on Earth!"
                      : "I'm joining the mission to sneak Nouns into the happiest place on Earth!";
                    
                    const castEmbed: [string] = ["https://sneakymouse.wtf"];
                    
                    const result = await sdk.actions.composeCast({
                      text: castText,
                      embeds: castEmbed
                    });
                    
                    if (result?.cast) {
                      console.log('✅ Cast compartilhado com sucesso:', result.cast.hash);
                    } else {
                      console.log('ℹ️ Usuário cancelou o cast');
                    }
                  } catch (error) {
                    console.error('❌ Erro ao compartilhar cast:', error);
                  }
                  // Sempre fechar TODOS os pop-ups após share (sucesso, cancelamento ou erro)
                  closeAllPopups();
                }}
                style={{
                  background: '#f9d70b',
                  border: 'none',
                  borderRadius: '0px',
                  padding: '2px 11px 0px 11px',
                  fontSize: '36px',
                  fontWeight: 'bold',
                  color: '#000000',
                  cursor: 'pointer',
                  fontFamily: 'Chalkboard, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '-1px',
                  transform: 'rotate(-4deg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '1.2',
                  marginTop: '-126px',
                  marginLeft: '39px',
                  height: 'fit-content',
                  textAlign: 'center'
                }}
              >
                <span style={{ position: 'relative', top: '2px' }}>SHARE NOW</span>
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  )
}
